import { uploadOfflineImageAttachments } from '../uploadAttachments';
import { deepCopy } from '../utils';

const isLocalUri = (value) => typeof value === 'string' && !value.startsWith('http://') && !value.startsWith('https://');

// PHOTO answers hold arrays of media links; while offline the media picker
// returns local device URIs, which must be uploaded before the queued
// submission is replayed (mirrors updateTicketPhotos).
const getLocalPhotoUris = (answers) => {
	return (answers || []).flatMap((answer) => {
		// Only PHOTO answers contain URI-like strings; option arrays (checkbox
		// groups) never start with a scheme, so filter on URI shape.
		if (!Array.isArray(answer?.value)) {
			return [];
		}

		return answer.value.filter(value => isLocalUri(value) && value.includes('://'));
	});
};

/**
 * Offline operation config for the submitChecklist mutation.
 *
 * Queues checklist submissions (leave warehouse, vehicle end of day, self
 * debrief) when offline, uploading any locally captured photos when the queue
 * flushes. There is no existing cache entity to patch, so no optimistic cache
 * update is applied — the My day board derives the queued state from the
 * offline queue entries instead.
 */
const submitChecklistConfig = {
	serializeVariables: ({ variables }) => deepCopy(variables),
	extractAttachments: ({ variables }) => {
		return getLocalPhotoUris(variables?.answers).map((localUri, index) => ({
			type: 'image',
			localUri,
			index,
		}));
	},
	createMeta: async ({ variables, context = {}, helpers }) => {
		const offlineMeta = context.offlineMeta || {};

		return {
			type: 'submitChecklist',
			purpose: offlineMeta.purpose || null,
			checklistTitle: offlineMeta.checklistTitle || null,
			date: variables?.date || null,
			serviceRunId: variables?.serviceRun || null,
			s3Path: 'images/checklists/',
			serverEndpoint: helpers?.getServerEndpoint ? await helpers.getServerEndpoint() : null,
		};
	},
	prepareVariables: async ({ entry }) => {
		const clonedVariables = deepCopy(entry.variables);
		const attachments = entry.attachments || [];

		if (!attachments.length) {
			return clonedVariables;
		}

		const uploaded = await uploadOfflineImageAttachments({ attachments, meta: entry.meta });
		const remoteUrlByLocalUri = new Map(uploaded.map(file => [file.localUri, file.remoteUrl]));
		const extractedLocalUris = new Set(attachments.map(attachment => attachment.localUri));

		clonedVariables.answers = (clonedVariables.answers || []).map((answer) => {
			if (!Array.isArray(answer?.value)) {
				return answer;
			}

			return {
				...answer,
				value: answer.value
					.map(value => remoteUrlByLocalUri.get(value) || value)
					// Safety net: never send a device URI to the server.
					.filter(value => !extractedLocalUris.has(value)),
			};
		});

		return clonedVariables;
	},
	getOfflineResult: ({ variables, queueEntry }) => ({
		submitChecklist: {
			__typename: 'ChecklistSubmission',
			id: null,
			purpose: queueEntry.meta?.purpose || null,
			date: variables?.date || null,
			offlineQueuedAt: queueEntry.createdAt,
			offline: true,
		},
	}),
};

export default submitChecklistConfig;
